import React, { useContext } from "react";
import XDate from "xdate";
import _ from "lodash";
import { alertSuccess, alertError } from "Utils/alerts";
import withFormState from "Components/withFormState";
import SimpleSelect from "Components/simpleSelect/SimpleSelect";
import Toggle from "Components/toggle/Toggle";
import Select from "Components/select/Select";
import Button from "Components/button/Button";
import DayPicker from "Components/dayPicker/DayPicker";
import { convertFileToBase64 } from "Utils/file";
import { formatServerErrors } from "Utils/serverFormats";
import ReactDropzone from "react-dropzone";
import CustomerSelect from "Components/customerSelect/CustomerSelect";

import WarningBanner from "Components/warningBanner/WarningBanner";

import DropdownIndicator from "Components/customArrowSelect/CustomArrowSelect";
import { Icon, upload, file, mxChange } from "Icons/index";
import { t } from "i18next";
import cx from "classnames";
import s from "./newMigrations.scss";
import { AuthContext } from "../../App";
import { useApiMutation, useApiQuery } from "../../hooks";

const formInitialState = {
  preset: null,
  first_execution_date: new XDate().toDate(),
  first_execution_hour: new XDate().addHours(1).getHours(),
  second_execution_date: {
    value: undefined,
    label: t("select.value.never"),
  },
  execute_now: false,
  file: "",
  has_masteruser: false,
  customer_code: "",
};

const MigrationsNewScreen = (props) => {
  const { formData, formErrors } = props;
  const { isLoggedUserAdmin } = useContext(AuthContext);

  const offsetHours = _.compact(
    _.times(23, (i) => {
      // hide 1 hour option
      if (i === 0) return null;

      return {
        value: i + 1,
        label: `${i + 1} ${i === 0 ? t("select.value.hour") : t("select.value.hours")}`,
      };
    })
  );

  const secondOffsetHourOptions = [
    {
      value: undefined,
      label: t("select.value.never"),
    },
    ...offsetHours,
  ];
  const hours = _.times(24, (i) => ({ value: i, label: `${i}:00` }));

  const {
    isLoading: isPresetLoading,
    data: presets,
    refreshData: refreshMigrations,
  } = useApiQuery("getMigrationPresets");
  const { mutate: createMigration } = useApiMutation("createMigration");

  const validate = (values) => {
    const errors = {};

    if (isLoggedUserAdmin() && !values.customer_code) {
      errors.customer_code = t("errorMsg.required");
    }
    if (!values.file) {
      errors.file = t("errorMsg.required");
      window.alert(t("alert.missingFile"));
    }
    if (!values.preset) {
      errors.preset = t("errorMsg.required");
    }

    if (!values.execute_now) {
      if (!values.first_execution_date) {
        errors.first_execution_date = t("errorMsg.required");
      }

      if (values.first_execution_hour == null) {
        errors.first_execution_hour = t("errorMsg.required");
      }

      const firstExecDate = new XDate(values.first_execution_date).clearTime().clone();
      const firstExecDateTime = firstExecDate.addHours(values.first_execution_hour || 0).clone();

      const dateIsPassed = firstExecDateTime.diffHours(new XDate()) > 0;

      if (dateIsPassed) errors.first_execution_hour = "is in the past";
    }

    if (!values.second_execution_date) {
      errors.second_execution_date = t("errorMsg.required");
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    props.setFormLoading();

    const errors = validate(formData);
    if (Object.keys(errors).length > 0) {
      props.setFormErrors(errors);
      props.unsetFormLoading();
      return;
    }

    const {
      file,
      preset,
      execute_now,
      first_execution_date,
      first_execution_hour,
      second_execution_date,
      has_masteruser,
      customer_code,
    } = formData;

    const payload = {
      preset,
      execute_now,
      has_masteruser,
      ...(isLoggedUserAdmin() && { customer_code }),
    };

    const secondOffset = second_execution_date;
    if (execute_now) {
      payload.second_execution_date = new XDate().addHours(secondOffset).toDate();
    } else {
      const firstRunDate = new XDate(first_execution_date).clearTime().addHours(first_execution_hour).toDate();

      payload.first_execution_date = firstRunDate;
      payload.second_execution_date = new XDate(firstRunDate).addHours(secondOffset).toDate();
    }

    try {
      const content = await convertFileToBase64(file);
      Object.assign(payload, {
        csv_file_content: content,
        csv_file_name: file.name,
        csv_file_size: file.size,
      });

      await createMigration(payload);
      await refreshMigrations();
      alertSuccess(t("alert.migrationCreateSuccess"));
      resetAndGoBack();
    } catch (error) {
      alertError(t("alert.operationError"));
      const serverErrors = await formatServerErrors(error);
      props.setFormErrors(serverErrors);
    } finally {
      props.unsetFormLoading();
    }
  };

  const onDrop = (files, reject) => {
    if (files.length > 1 || reject.length > 0) {
      window.alert(t("alert.invalidFileCSVFormat"));
    } else {
      props.setFormFieldValue({ file: files[0] });
    }
  };

  const resetAndGoBack = () => {
    props.resetForm();
    props.history.push("/migrations");
  };

  const resetFileField = () => {
    props.setFormFieldError("file", null);
    props.setFormFieldValue({ file: undefined });
  };

  return (
    <div className="formsPage">
      <div className={cx("sectionTitle", "titleFP")}>{t("migrations.add.title")}</div>

      <div className="wrapScrollFP">
        <div className="infoFP">
          <div className="sectionDesc">{t("migrations.add.desc")}</div>
          <div className="wrapBtnFP">
            <Button className="btnDocsFP" docsKey="migrations_add" color="docs" sizeSmall>
              button.docs
            </Button>
            <div className="btnFileFP">
              <a href={`${BASE_URL_PUBLIC_ASSETS}/Migrations-examples.zip`} download>
                {t("action.download.example.csv")}
              </a>
            </div>
          </div>
        </div>

        <form className="wrapFormsFP" method="post" onSubmit={handleSubmit}>
          <div className="formsFP">
            {isLoggedUserAdmin() && (
              <CustomerSelect
                errors={formErrors.customer_code}
                className={"inputFull2"}
                label={t("input.label.customer")}
                value={formData.customer_code}
                onChange={(v) => {
                  props.setFormFieldValue({ customer_code: v?.value });
                  props.setFormErrors({ ...formErrors, customer_code: null });
                }}
              />
            )}

            <Select
              className={cx("inputFull2", "inputAddMargin")}
              components={{ DropdownIndicator }}
              name="preset"
              label={t("input.label.serverSource")}
              options={_.map(presets, (v) => ({
                value: v.name,
                label: v.label,
              }))}
              value={formData.preset}
              errors={formErrors.preset}
              onChange={props.setFormFieldValue}
              isLoading={isPresetLoading}
              required
            />

            <Toggle
              className={cx("inputFull2", "inputAddMargin")}
              name="has_masteruser"
              label={t("input.label.migration.masteruser")}
              checked={formData.has_masteruser || false}
              onChange={props.setFormFieldValue}
            />

            <div className={cx("wrapDropzone", "inputFull2")}>
              <label className={cx("labelDZ", s.required)}>{t("input.label.migration.selectFile")}</label>
              {formData.file || formErrors.file ? (
                <FilePreview formData={formData} formErrors={formErrors} resetFileField={resetFileField} />
              ) : (
                <Dropzone onDrop={onDrop} />
              )}
            </div>

            <section className={"inputFull2"}>
              <h2 className={s.titleStep}>{t("migration.step.title")}</h2>
              <div className={s.wrapStep}>
                <div className={s.step}>
                  <h3 className={s.labelStep}>{t("migration.step.label1")}</h3>
                  <div className={cx("sectionDesc", s.textStep)}>{t("migration.step.text1")}</div>
                  <Toggle
                    className={cx("inputFull2", "inputAddMargin")}
                    name="execute_now"
                    label={t("input.label.migration.executeNow")}
                    checked={formData.execute_now || false}
                    onChange={props.setFormFieldValue}
                    hint={t("input.hint.migration.executeNow")}
                  />
                  <div
                    className={cx(s.wrapDatePicker, {
                      [s.withErrors]: formErrors.first_execution_date || formErrors.first_execution_hour,
                    })}
                  >
                    <label className={s.labelDatePikcer}>{t("input.label.migration.startDate")}</label>

                    <div className={s.datePikcer}>
                      <DayPicker
                        className={s.dayPicker}
                        name="first_execution_date"
                        value={formData.first_execution_date}
                        errors={formErrors.first_execution_date}
                        onChange={props.setFormFieldValue}
                        minDate={new XDate().toDate()}
                        disabled={formData.execute_now}
                        required
                      />

                      <SimpleSelect
                        className={s.hoursSelect}
                        options={hours}
                        disabled={formData.execute_now}
                        name="first_execution_hour"
                        errors={formErrors.first_execution_hour}
                        value={formData.first_execution_hour}
                        onChange={props.setFormFieldValue}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className={cx(s.wrapStep, s.wrapStepInfo)}>
                <div className={s.step}>
                  <Icon className={s.iconInfoStep} svg={mxChange} width="4rem" height="4rem" />

                  <div>
                    <h3 className={s.labelStep}>{t("migration.step.label2")}</h3>
                    <div className={cx("sectionDesc", s.textStep)}>{t("migration.step.text2")}</div>
                  </div>
                </div>
              </div>

              <div className={s.wrapStep}>
                <div className={s.step}>
                  <h3 className={s.labelStep}>{t("migration.step.label3")}</h3>
                  <div className={cx("sectionDesc", s.textStep)}>{t("migration.step.text3")}</div>
                  <div className={s.warningStep}>{t("migration.step.warning3")}</div>
                  <SimpleSelect
                    options={secondOffsetHourOptions}
                    label={t("input.label.migration.secondExecHour")}
                    name="second_execution_date"
                    value={formData.second_execution_date}
                    errors={formErrors.second_execution_date}
                    onChange={props.setFormFieldValue}
                    required
                  />
                </div>
              </div>
            </section>
          </div>

          {formErrors.onForm && (
            <div className="warningMsgFP">
              <WarningBanner msg={formErrors.onForm} color="red" />
            </div>
          )}

          <div className="footerFP">
            <Button color="text" onClick={resetAndGoBack}>
              button.cancel
            </Button>
            <Button color="green" type="submit" loading={props.isFormLoading}>
              button.migrate
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default withFormState(MigrationsNewScreen, formInitialState);

const Dropzone = ({ onDrop }) => {
  const acceptedMIMETypes = ["text/plain", "application/vnd.ms-excel", "text/x-csv", "text/csv"];
  return (
    <ReactDropzone onDrop={onDrop} accept={acceptedMIMETypes}>
      {(params) => {
        return (
          <div
            {...params.getRootProps()}
            className={cx(
              "py-10 flex items-center justify-center rounded bg-[--bg] cursor-pointer hover:bg-[--bg-d5] transition-all",
              {
                active: params.isDragActive,
                reject: params.isDragReject,
              }
            )}
          >
            <input {...params.getInputProps()} />
            <Icon className="iconDZ" svg={upload} width="1rem" height="1rem" />
            <div className="textDZ">{t("input.label.dragHere")}</div>
          </div>
        );
      }}
    </ReactDropzone>
  );
};
const FilePreview = ({ formErrors, formData, resetFileField }) => {
  return (
    <div
      className={cx("py-10 flex gap-4 flex-col items-center justify-center rounded bg-[--accent-l35]", "inputFull2", {
        withError: formErrors.file,
      })}
    >
      {formErrors.file ? (
        <div className={"errorDZ"}>
          {/* <Icon className="iconDZ" color="red" svg={file} width="3rem" height="3rem" /> */}

          <h3 className={s.invalidFileLabel}>{t("qbmove.file_errors")}</h3>
          <div className={cx("sectionDesc")}>{formErrors.file}</div>
        </div>
      ) : (
        <div className="flex items-center gap-1 max-w-[70%]">
          <Icon className="iconDZ" svg={file} width="1rem" height="1rem" />
          <div className="textDZ">{formData.file.name}</div>
        </div>
      )}

      <Button className="btnDZ" color="outline" onClick={resetFileField} sizeSmall>
        button.change
      </Button>
    </div>
  );
};
